/**
 * Manages the WebSocket connection for live streaming transcription.
 *
 * Responsibilities:
 * - Open / close the streaming WebSocket
 * - Exponential back-off reconnect on transient disconnects (pod restarts etc.)
 * - Intent discrimination: distinguish intentional server stop from transient disconnect
 * - Expose helpers for the SSE event handler in RecordingControls to signal intent
 */
import type { IClientInfo } from "@lexicanon/shared"
import { useCallback, useEffect, useRef, useState } from "react"
import { getClientInfo } from "../lib/client-info"
import { wsOrigin } from "../lib/runtime-origin"

type TRecordingState = "idle" | "recording" | "paused" | "finalizing"
type TRecordingMode = "batch" | "streaming"
type TStreamChannel = "room" | "remote"

interface IProps {
  meetingId: string
  companyId: string | null | undefined
  languages: string[]
  /** Transcription adapter to use (e.g. "speechmatics", "deepgram"). Omit to use server default. */
  backend?: string | null
  /** Capture channel. "remote" tags the system-loopback stream (Tauri hybrid mode); omit ⇒ "room". */
  channel?: TStreamChannel
  /**
   * Returns the cached WebM/EBML init header chunk (the first MediaRecorder blob),
   * or null if none has been captured yet. On an auto-reconnect the same
   * MediaRecorder keeps running and emits only mid-stream (headerless) Opus frames,
   * so the fresh server session can't parse the stream unless the header is replayed
   * first. Must be a stable reference (useCallback). Omit for channels with no cache.
   */
  getHeaderChunk?: () => Blob | null
  /** Ref to the current recording state — read inside WS callbacks to avoid stale closures. */
  stateRef: React.MutableRefObject<TRecordingState>
  attachWs: (ws: WebSocket) => void
  detachWs: () => void
  setMode: (mode: TRecordingMode) => void
  setError: (err: string) => void
}

interface IUseStreamingWsResult {
  wsRef: React.MutableRefObject<WebSocket | null>
  wsReconnecting: boolean
  /** Set to true by handleFinish so the onclose handler knows not to reconnect. */
  serverStoppedRef: React.MutableRefObject<boolean>
  /** Open a new WebSocket. Returns the socket (already stored in wsRef). */
  openStreamingWs: () => WebSocket | null
  /** Reset all reconnect counters/flags — call at the start of a fresh recording. */
  resetWsState: () => void
  /** Call when SSE delivers type="streaming_stopped". */
  handleStreamingStopped: () => void
  /** Call when SSE delivers type="streaming_fallback". */
  handleStreamingFallback: () => void
}

export function useStreamingWs({
  meetingId,
  companyId,
  languages,
  backend,
  channel,
  getHeaderChunk,
  stateRef,
  attachWs,
  detachWs,
  setMode,
  setError,
}: IProps): IUseStreamingWsResult {
  const wsRef = useRef<WebSocket | null>(null)
  const wsReconnectAttemptsRef = useRef(0)
  const wsReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const serverStoppedRef = useRef(false)
  const serverFallbackRef = useRef(false)
  const mountedRef = useRef(true)
  // Recording-client descriptor (desktop vs browser + OS + version), resolved once
  // on mount and sent on the room-channel connect so the meeting can be labelled
  // beyond the bare "browser" source. Resolves fast (only the desktop branch awaits
  // Tauri's getVersion); absent on the first connect just means the server keeps
  // the prior/empty value — harmless, the UI falls back to `source`.
  const clientInfoRef = useRef<IClientInfo | null>(null)
  const [wsReconnecting, setWsReconnecting] = useState(false)

  // Stable refs break the circular dependency between openStreamingWs and scheduleWsReconnect.
  // openStreamingWs.onclose calls scheduleWsReconnectRef.current, which always points to the
  // latest scheduleWsReconnect; and scheduleWsReconnect calls openStreamingWsRef.current.
  const scheduleWsReconnectRef = useRef<() => void>(() => {})

  // Resolve the client descriptor once (room channel only — the remote channel is
  // a second connection from the same client, the server stores it from the room).
  useEffect(() => {
    if (channel === "remote") return
    let cancelled = false
    getClientInfo()
      .then((info) => {
        if (!cancelled) clientInfoRef.current = info
      })
      .catch(() => {})
    return (): void => {
      cancelled = true
    }
  }, [channel])
  const openStreamingWsRef = useRef<() => WebSocket | null>(() => null)

  const scheduleWsReconnect = useCallback((): void => {
    const attempt = wsReconnectAttemptsRef.current + 1
    wsReconnectAttemptsRef.current = attempt

    if (attempt > 4) {
      setWsReconnecting(false)
      setMode("batch")
      setError("Live transcription could not reconnect after pod restart. Switched to batch mode.")
      return
    }

    setWsReconnecting(true)
    const delayMs = attempt === 1 ? 5_000 : attempt === 2 ? 10_000 : attempt === 3 ? 20_000 : 40_000

    wsReconnectTimerRef.current = setTimeout(() => {
      wsReconnectTimerRef.current = null
      // Component already unmounted (user navigated away mid-back-off) — do NOT
      // resurrect a socket against a dead component; it would leak for the page
      // lifetime. The unmount cleanup below clears this timer in the common case;
      // this guard covers the in-flight-callback race.
      if (!mountedRef.current) return
      if (stateRef.current === "recording" || stateRef.current === "paused") {
        openStreamingWsRef.current()
      } else {
        setWsReconnecting(false)
      }
    }, delayMs)
  }, [setMode, setError, stateRef])

  scheduleWsReconnectRef.current = scheduleWsReconnect

  const openStreamingWs = useCallback((): WebSocket | null => {
    const langParam = languages.length > 0 ? languages.join(",") : "multi"
    const params = new URLSearchParams({ language: langParam })
    if (backend) params.set("backend", backend)
    if (channel === "remote") params.set("channel", "remote")
    if (channel !== "remote" && clientInfoRef.current) {
      params.set("client", JSON.stringify(clientInfoRef.current))
    }
    const url = `${wsOrigin()}/api/companies/${companyId}/meetings/${meetingId}/stream-audio?${params}`
    if (channel === "remote") console.info("[remote-capture] connecting remote WS:", url)
    const ws = new WebSocket(url)

    ws.onopen = (): void => {
      if (channel === "remote") console.info("[remote-capture] remote WS OPEN")
      attachWs(ws)
      // A reconnect (pod restart mid-recording) opens a brand-new server session,
      // but the still-running MediaRecorder only emits mid-stream Opus frames — no
      // WebM/EBML header. Replay the cached first chunk (the header) on the fresh
      // socket before those frames arrive, or the new session's ffmpeg can never
      // parse the stream and live transcription silently dies. Gated on the reconnect
      // attempt counter (>0 only on an auto-reconnect open, still 0 on the first-ever
      // open and on the pause/resume reopen — that path replays the header itself, so
      // this gate keeps us from double-sending it). Never replay once the server has
      // signalled stop/fallback.
      if (wsReconnectAttemptsRef.current > 0 && !serverStoppedRef.current && !serverFallbackRef.current) {
        const headerChunk = getHeaderChunk?.() ?? null
        if (headerChunk && ws.readyState === WebSocket.OPEN) {
          headerChunk.arrayBuffer().then((buf) => {
            if (ws.readyState === WebSocket.OPEN) ws.send(buf)
          })
        }
      }
      wsReconnectAttemptsRef.current = 0
      setWsReconnecting(false)
    }

    ws.onclose = (ev: CloseEvent): void => {
      if (channel === "remote") {
        console.warn("[remote-capture] remote WS CLOSED", { code: ev.code, reason: ev.reason, wasClean: ev.wasClean })
      }
      if (wsRef.current === ws) {
        wsRef.current = null
        detachWs()
        if (stateRef.current === "recording" || stateRef.current === "paused") {
          if (serverStoppedRef.current || serverFallbackRef.current) {
            setMode("batch")
            if (serverFallbackRef.current) {
              setError("Live transcription failed after reconnects. Switched to batch mode.")
            }
          } else {
            scheduleWsReconnectRef.current()
          }
        }
      }
    }

    ws.onerror = (): void => {
      if (channel === "remote") console.error("[remote-capture] remote WS ERROR (connection failed)")
    }

    wsRef.current = ws
    return ws
  }, [
    meetingId,
    companyId,
    attachWs,
    detachWs,
    languages,
    backend,
    channel,
    getHeaderChunk,
    setMode,
    setError,
    stateRef,
  ])

  openStreamingWsRef.current = openStreamingWs

  const resetWsState = useCallback((): void => {
    wsReconnectAttemptsRef.current = 0
    serverStoppedRef.current = false
    serverFallbackRef.current = false
    setWsReconnecting(false)
    if (wsReconnectTimerRef.current) {
      clearTimeout(wsReconnectTimerRef.current)
      wsReconnectTimerRef.current = null
    }
  }, [])

  const handleStreamingStopped = useCallback((): void => {
    serverStoppedRef.current = true
    setWsReconnecting(false)
    if (wsReconnectTimerRef.current) {
      clearTimeout(wsReconnectTimerRef.current)
      wsReconnectTimerRef.current = null
    }
  }, [])

  const handleStreamingFallback = useCallback((): void => {
    serverFallbackRef.current = true
    setWsReconnecting(false)
    if (wsReconnectTimerRef.current) {
      clearTimeout(wsReconnectTimerRef.current)
      wsReconnectTimerRef.current = null
    }
    setMode("batch")
    setError("Live transcription fell back to batch mode after reconnect failures.")
  }, [setMode, setError])

  // Unmount safety net: clear any pending reconnect timer and close a live socket
  // so neither leaks past the component. Without this, a drop during the back-off
  // window left a timer that fired post-unmount and opened a zombie WebSocket.
  useEffect(() => {
    return () => {
      mountedRef.current = false
      if (wsReconnectTimerRef.current) {
        clearTimeout(wsReconnectTimerRef.current)
        wsReconnectTimerRef.current = null
      }
      if (wsRef.current) {
        try {
          // 1001 = going away. A remount opens a new socket that reattaches
          // to the live server session; identity checks ignore this close.
          // A real leave still starts the unexpected-disconnect grace.
          wsRef.current.close(1001)
        } catch {
          /* already closed */
        }
        wsRef.current = null
      }
    }
  }, [])

  return {
    wsRef,
    wsReconnecting,
    serverStoppedRef,
    openStreamingWs,
    resetWsState,
    handleStreamingStopped,
    handleStreamingFallback,
  }
}
